<template>
  <b-container>
    <b-row>
      <b-col cols="12">
        <b-row class="justify-content-between mb-3">
          <b-col cols="3" sm="6" md="3">
            <label>Rechercher</label>
            <b-form-input placeholder="Tapez pour rechercher" @input="search" />
          </b-col>
          <b-col cols="3" sm="6" md="3">
            <label>par page</label>
            <b-select
              v-model="pagination.pageSize"
              :options="[10, 20, 50, 100]"
              @input="getMeetings"
            ></b-select>
          </b-col>
        </b-row>
      </b-col>
      <b-col cols="12">
        <b-table
          class="agv-table td-middle"
          bordered
          :busy="isBusy"
          :items="meetings"
          :fields="fields"
          responsive
          show-empty
        >
          <template #table-busy>
            <appIsLoading />
          </template>
          <template #empty>
            <span>{{ "Aucune convocation..." }}</span>
          </template>
          <template v-slot:cell(name)="row">
            <div class="d-flex flex-column">
              <span>{{ row.item.name }}</span>
              <span
                v-if="
                  (row.item.allowed_modalities ? row.item.allowed_modalities.video : false) &&
                  row.item.external_video_solution &&
                  row.item.show_connection_details
                "
              >
                <b-link
                  class="text-decoration-none"
                  target="_blank"
                  :href="row.item.external_video_solution"
                >
                  {{ row.item.external_video_solution }}
                </b-link>
              </span>
            </div>
          </template>
          <template v-slot:cell(meeting_date)="row">
            {{ formatDate(row.item.meeting_date) }}
          </template>
          <template v-slot:cell(actions)="row">
            <!-- NOTE: This is available when the meeting has video enabled and the status is invitationSent or started  -->
            <b-button
              v-if="
                (row.item.allowed_modalities ? row.item.allowed_modalities.video : false) &&
                (row.item.status == meetingStatuses.invitationSent ||
                  row.item.status == meetingStatuses.started)
              "
              class="tablebutton btn-green btn-duplicate mx-1"
              @click="accessMeeting(row.item)"
            >
              <font-awesome-icon icon="play" />
            </b-button>
            <!-- NOTE: Meeting completed -->
            <span v-if="checkIfMeetingIsCompleted(row.item)">
              <font-awesome-icon icon="check-circle" class="text-success" />
              Terminé
            </span>

            <!-- NOTE: Hide documents if is contact_id or meeting_guest_id (DEV-3494)-->
            <div v-if="building_owner_id && row.item.building_owner_mandate">
              <div class="d-flex flex-column">
                <div
                  v-if="
                    row.item.building_owner_mandate.docage_id &&
                    getDocageSignDate(row.item.building_owner_mandate.docage_id)
                  "
                  class="my-2"
                >
                  Le document a été voté et signé le
                  {{ getDocageSignDate(row.item.building_owner_mandate.docage_id) }}
                </div>
                <div
                  v-if="
                    !isVotingAllowed(row.item.last_reception_date) &&
                    row.item.building_owner_mandate.docage_id &&
                    getDocageSignDate(row.item.building_owner_mandate.docage_id)
                  "
                >
                  Le vote était possible jusqu'à
                  {{ $moment(row.item.last_reception_date).format("DD MMMM YYYY HH:mm") }}
                  <hr />
                </div>
                <div
                  v-if="
                    !checkIfMeetingIsCompleted(row.item) &&
                    isVotingAllowed(row.item.last_reception_date)
                  "
                >
                  <b-button
                    class="tablebutton btn-duplicate mx-1"
                    :to="{ name: 'correspondence-vote-and-sign', params: { id: row.item.id } }"
                  >
                    <font-awesome-icon icon="vote-yea" />
                    <span class="h5 ml-1">Voter et signer la correspondance</span>
                  </b-button>
                  <hr />
                </div>
                <!-- Invitation documents - shown after invitation is sent -->
                <div v-if="isInvitationSent(row.item.status)">
                  <a
                    v-if="row.item.building_owner_mandate.voting_form"
                    :href="PUBLIC_PATH + row.item.building_owner_mandate.voting_form.file_path"
                    target="_blank"
                    class="d-flex mb-2"
                  >
                    <font-awesome-icon icon="file-pdf" size="2x" class="green" />
                    <span class="ml-2 align-self-center">Correspondance</span>
                  </a>
                  <a
                    v-if="row.item.building_owner_mandate.pouvoir"
                    :href="PUBLIC_PATH + row.item.building_owner_mandate.pouvoir.file_path"
                    target="_blank"
                    class="d-flex mb-2"
                  >
                    <font-awesome-icon icon="file-pdf" size="2x" class="green" />
                    <span class="ml-2 align-self-center">Pouvoir</span>
                  </a>
                  <a
                    v-if="row.item.building_owner_mandate.invitation"
                    :href="PUBLIC_PATH + row.item.building_owner_mandate.invitation.file_path"
                    target="_blank"
                    class="d-flex mb-2"
                  >
                    <font-awesome-icon icon="file-pdf" size="2x" class="green" />
                    <span class="ml-2 align-self-center">Convocation</span>
                  </a>
                  <a
                    v-if="row.item.agenda_media"
                    :href="PUBLIC_PATH + row.item.agenda_media.file_path"
                    target="_blank"
                    class="d-flex mb-2"
                  >
                    <font-awesome-icon icon="file-pdf" size="2x" class="green" />
                    <span class="ml-2 align-self-center">Ordre du jour</span>
                  </a>
                </div>
                <!-- PV documents - shown after PV is sent -->
                <div v-if="isPvSent(row.item.status) && row.item.signed_pv_media">
                  <hr />
                  <a
                    :href="PUBLIC_PATH + row.item.signed_pv_media.file_path"
                    target="_blank"
                    class="d-flex mb-2"
                  >
                    <font-awesome-icon icon="file-pdf" size="2x" class="green" />
                    <span class="ml-2 align-self-center">PV signé</span>
                  </a>
                </div>
              </div>
            </div>
          </template>
        </b-table>
      </b-col>
      <b-col cols="12" class="text-center">
        <b-row>
          <b-col cols="4" class="m-auto d-flex justify-content-center">
            <b-pagination
              v-model="pagination.page"
              :total-rows="pagination.rowCount"
              :per-page="pagination.pageSize"
              aria-controls="mt-table"
              @input="getMeetings"
            ></b-pagination>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import MeetingORM from "@/store/orm-axios/models/Meeting";
import ContactORM from "@/store/orm-axios/models/Contact";
import DocageORM from "@/store/orm-axios/models/Docage";

import APILinks from "@/links/links.js";
import MeetingsMercure from "@/mixins/mercure-live-changes/meetings";

export default {
  components: {
    appIsLoading: () => import("@/components/Snippets/IsLoading"),
  },
  mixins: [MeetingsMercure],
  data() {
    return {
      isBusy: true,
      PUBLIC_PATH: "",
      fields: [
        {
          label: "Immeuble",
          key: "building.name",
          sortable: false,
        },
        {
          label: "Nom de la convocation",
          key: "name",
          sortable: false,
        },
        {
          label: "Date",
          key: "meeting_date",
          sortable: false,
        },
        {
          label: "Actions",
          key: "actions",
          class: "justify-content-center",
        },
      ],
      pagination: {
        page: 1,
        pageSize: 10,
        rowCount: 0,
        pageCount: 0,
      },
      tableHeaderSettings: {
        filter: [
          {
            column: "name",
            value: "",
          },
        ],
        sort: {
          by: "created",
          dir: "desc",
        },
      },
    };
  },
  computed: {
    meetings() {
      return MeetingORM.query()
        .where("building_id", this.building_id)
        .with("building|agenda_media|signed_pv_media")
        .with("building_owner_mandate", query => {
          query
            .where("building_owner_id", this.building_owner_id)
            .with("voting_form|pouvoir|invitation");
        })
        .limit(this.pagination.pageSize)
        .orderBy("meeting_date", "desc")
        .get();
    },
    timezone() {
      return localStorage.getItem("timezone");
    },
  },
  async created() {
    try {
      this.PUBLIC_PATH = APILinks.PUBLIC_PATH();

      if (this.contact_id != null) {
        await ContactORM.api()
          .GET(this.contact_id)
          .catch(err => {
            console.error(err);
          });
      }

      await this.getMeetings();
      if (this.timezone) {
        this.$moment.tz.setDefault(this.timezone);
      }
    } catch (err) {
      console.error(err);
      this.isBusy = false;
    }
  },
  methods: {
    async getMeetings() {
      this.isBusy = true;
      MeetingORM.deleteAll();
      let queryParams = this.prepareQueryParams();
      await MeetingORM.api()
        .GET_BY_BUILDING_WITH_BO_DOCUMENTS(this.building_id, queryParams)
        .then(res => {
          this.pagination.rowCount = res.response.data["hydra:totalItems"];
          this.isBusy = false;
        })
        .catch(err => {
          console.error(err);
          this.isBusy = false;
        });
    },
    async accessMeeting(item) {
      if (item.status != this.meetingStatuses.started) {
        if (
          this.$moment()
            .add(15, "minute")
            .isAfter(this.$moment(item.meeting_date).utc().locale("fr"))
        ) {
          this.currentMeeting = item;
          this.$router.push({
            params: {
              id: item.id,
            },
            name: "meeting-start",
          });
        } else {
          await this.$fire({
            type: "warning",
            title: "La réunion n'a pas encore commencé.",
            text: "Veuillez revenir à la date et à l'heure prévues.",
            timer: 5000,
          });
        }
      } else {
        this.currentMeeting = item;
        this.$router.push({
          params: {
            id: item.id,
          },
          name: "meeting-start",
        });
      }
    },
    formatDate(date) {
      return this.$moment(date).format("DD MMMM YYYY HH:mm");
    },
    prepareQueryParams() {
      var queryParams = {
        filter: [],
        sort: {},
      };
      if (this.tableHeaderSettings.sort.by && this.tableHeaderSettings.sort.dir) {
        queryParams.sort["by"] = this.tableHeaderSettings.sort.by;
        queryParams.sort["dir"] = this.tableHeaderSettings.sort.dir;
      }
      this.tableHeaderSettings.filter.forEach(filter => {
        if (!!filter.value || filter.value === 0) {
          var preparedFilter = {
            column: filter.column,
            value: filter.value,
          };
          queryParams.filter.push(preparedFilter);
        }
      });

      queryParams.page = this.pagination.page;
      queryParams.pageSize = this.pagination.pageSize;
      queryParams.itemsPerPage = this.pagination.pageSize;

      return queryParams;
    },
    search(e) {
      if (e.length >= 3) {
        this.tableHeaderSettings.filter[0].value = e;
        this.getMeetings();
      } else if (e.length == 0) {
        this.tableHeaderSettings.filter[0].value = "";
        this.getMeetings();
      }
    },
    checkIfMeetingIsCompleted(meeting) {
      return (
        meeting.status == this.meetingStatuses.completed ||
        meeting.status == this.meetingStatuses.pvReadyToBeSent ||
        meeting.status == this.meetingStatuses.pvInProgress ||
        meeting.status == this.meetingStatuses.pvSent
      );
    },
    isInvitationSent(status) {
      // Invitation documents visible after invitation is sent (status >= invitationSent)
      return (
        status == this.meetingStatuses.invitationSent ||
        status == this.meetingStatuses.started ||
        status == this.meetingStatuses.completed ||
        status == this.meetingStatuses.pvReadyToBeSent ||
        status == this.meetingStatuses.pvInProgress ||
        status == this.meetingStatuses.pvSent
      );
    },
    isPvSent(status) {
      // PV documents visible after PV is sent
      return status == this.meetingStatuses.pvSent;
    },
    isVotingAllowed(lastReceptionDate) {
      return this.$moment().isBefore(this.$moment(lastReceptionDate));
    },
    getDocageSignDate(docageId) {
      const docage = DocageORM.find(docageId);
      return docage && docage.sign_date
        ? this.$moment(docage.sign_date).format("DD MMMM YYYY HH:mm")
        : null;
    },
  },
};
</script>
